import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ChartBarIcon, ArrowLeftIcon, ArrowPathIcon,
  KeyIcon, CheckCircleIcon as CheckCircleOutline,
  ExclamationTriangleIcon, ClockIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { apiClient } from '../../lib/axios';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

interface DayBucket { date: string; count: number; success_count: number }
interface ByKey { key_id: string; key_name: string; total_requests: number; success_count: number }
interface TopEndpoint { endpoint: string; count: number }

interface AllAnalytics {
  total_requests: number;
  success_count: number;
  error_count: number;
  success_rate: number;
  requests_by_day: DayBucket[];
  by_key: ByKey[];
  top_endpoints: TopEndpoint[];
}

function StatCard({ label, value, sub, accent, icon: Icon }: {
  label: string; value: string | number; sub?: string;
  accent?: 'brand' | 'emerald' | 'red' | 'amber'; icon?: React.ElementType;
}) {
  const colors = {
    brand:   'bg-brand-500 text-slate-900 shadow-brand-500/20',
    emerald: 'bg-emerald-50 border border-emerald-100 text-emerald-900',
    red:     'bg-red-50 border border-red-100 text-red-900',
    amber:   'bg-amber-50 border border-amber-100 text-amber-900',
  };
  const base = accent ? colors[accent] : 'bg-white border border-slate-200';
  return (
    <div className={`rounded-2xl p-6 shadow-sm flex flex-col gap-3 ${base}`}>
      {Icon && (
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${accent === 'brand' ? 'bg-white/15' : 'bg-slate-100'}`}>
          <Icon className={`w-5 h-5 ${accent === 'brand' ? 'text-slate-900' : 'text-slate-500'}`} />
        </div>
      )}
      <div>
        <p className={`text-[10px] font-black uppercase tracking-wide ${accent === 'brand' ? 'text-slate-800' : 'text-slate-400'}`}>{label}</p>
        <p className={`text-3xl font-extrabold mt-0.5 leading-none ${accent === 'brand' ? 'text-slate-900' : ''}`}>{value}</p>
        {sub && <p className={`text-xs mt-1 font-medium ${accent === 'brand' ? 'text-slate-700' : 'text-slate-400'}`}>{sub}</p>}
      </div>
    </div>
  );
}

function BarChart({ data, label }: { data: DayBucket[]; label: string }) {
  const maxVal = Math.max(...data.map(d => d.count), 1);
  return (
    <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
      <p className="text-[10px] font-black text-slate-400 uppercase tracking-wide mb-5">{label}</p>
      <div className="flex items-end gap-1.5 h-32">
        {data.map(d => (
          <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group">
            <div className="w-full relative flex flex-col justify-end h-28">
              {d.count > 0 && (
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  <div className="bg-slate-900 text-slate-900 text-[10px] font-bold px-2 py-1 rounded-lg whitespace-nowrap">
                    {d.count} req
                  </div>
                </div>
              )}
              <div
                className="w-full rounded-t-md transition-all duration-300 bg-brand-500 group-hover:bg-brand-600"
                style={{ height: `${Math.round((d.count / maxVal) * 100)}%`, minHeight: d.count > 0 ? '4px' : '0' }}
              />
            </div>
            <span className="text-[9px] font-bold text-slate-400">
              {new Date(d.date + 'T00:00:00').toLocaleDateString('en', { month: 'short', day: 'numeric' })}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function APIAnalytics() {
  useDocumentTitle('API Analytics', 'Monitor your Localcontact API usage, success rates, and request trends across all keys.');
  const [data, setData] = useState<AllAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const fetchData = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    try {
      const res = await apiClient.get('/user/api-analytics');
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const rate = data?.success_rate ?? 0;
  const rateAccent = !data ? undefined
    : rate >= 90 ? 'emerald' as const
    : rate >= 70 ? 'amber' as const
    : 'red' as const;

  return (
    <div className="h-full flex flex-col bg-slate-50/50">
      <div className="flex-1 px-6 md:px-10 py-8 overflow-y-auto">
        <div className="max-w-[1200px] mx-auto flex flex-col gap-8">

          {/* Header */}
          <div className="flex items-center justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/api-access"
                className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-50 transition-colors shadow-sm"
              >
                <ArrowLeftIcon className="w-4 h-4" />
              </Link>
              <div>
                <h1 className="text-2xl font-extrabold text-slate-900 tracking-tight">API Analytics</h1>
                <p className="text-sm text-slate-500 mt-0.5">Aggregate usage across all your API keys, last 14 days.</p>
              </div>
            </div>
            <button
              onClick={() => fetchData(true)}
              disabled={refreshing}
              className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 px-4 py-2 rounded-xl text-sm font-bold hover:bg-slate-50 transition-colors shadow-sm disabled:opacity-50"
            >
              <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
            </button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-5">
              {[1, 2, 3, 4].map(i => (
                <div key={i} className="bg-white border border-slate-200 rounded-2xl p-6 h-32 animate-pulse" />
              ))}
            </div>
          ) : !data ? (
            <div className="bg-white border border-slate-200 rounded-2xl p-12 text-center shadow-sm">
              <ExclamationTriangleIcon className="w-10 h-10 text-slate-400 mx-auto mb-3" />
              <p className="text-slate-500 font-medium">Failed to load analytics. Check your API keys are set up.</p>
              <Link to="/dashboard/api-access" className="mt-3 inline-block text-brand-500 text-sm font-bold hover:underline">
                Go to API Access →
              </Link>
            </div>
          ) : (
            <>
              {/* Stat cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                <StatCard accent="brand" icon={ChartBarIcon} label="Total Requests" value={(data.total_requests ?? 0).toLocaleString()} sub="Last 14 days" />
                <StatCard accent="emerald" icon={CheckCircleOutline} label="Successful" value={(data.success_count ?? 0).toLocaleString()} sub="2xx responses" />
                <StatCard accent="red" icon={ExclamationTriangleIcon} label="Errors" value={(data.error_count ?? 0).toLocaleString()} sub="4xx / 5xx responses" />
                <StatCard accent={rateAccent} icon={ClockIcon} label="Success Rate" value={`${data.success_rate ?? 0}%`} sub="Overall health" />
              </div>

              {/* 14-day chart */}
              {data.requests_by_day.length > 0 && (
                <BarChart data={data.requests_by_day} label="Requests, Last 14 Days" />
              )}

              {/* Bottom grid: by key + top endpoints */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">

                {/* By key */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
                    <KeyIcon className="w-5 h-5 text-slate-400" />
                    <h3 className="text-base font-bold text-slate-900">Usage by Key</h3>
                  </div>
                  {data.by_key.length === 0 ? (
                    <div className="px-6 py-10 text-center text-slate-400 text-sm font-medium">No key usage recorded yet.</div>
                  ) : (
                    <div className="divide-y divide-slate-50">
                      {data.by_key.map(k => {
                        const totalReq = k.total_requests ?? 0;
                        const successReq = k.success_count ?? 0;
                        const rate = totalReq > 0 ? Math.round((successReq / totalReq) * 100) : 0;
                        return (
                          <div key={k.key_id} className="flex items-center justify-between px-6 py-4 hover:bg-slate-50 transition-colors group">
                            <div className="flex items-center gap-3 min-w-0">
                              <div className="w-8 h-8 rounded-lg bg-brand-50 flex items-center justify-center shrink-0">
                                <KeyIcon className="w-4 h-4 text-brand-500" />
                              </div>
                              <div className="min-w-0">
                                <p className="font-bold text-slate-900 text-sm truncate">{k.key_name ?? 'Unknown Key'}</p>
                                <p className="text-[11px] text-slate-400 font-medium">{totalReq.toLocaleString()} requests</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3 shrink-0">
                              <div className="w-20 h-1.5 bg-slate-100 rounded-full overflow-hidden hidden sm:block">
                                <div
                                  className={`h-full rounded-full ${rate >= 90 ? 'bg-emerald-500' : rate >= 70 ? 'bg-amber-500' : 'bg-red-500'}`}
                                  style={{ width: `${rate}%` }}
                                />
                              </div>
                              <span className={`text-[12px] font-extrabold ${rate >= 90 ? 'text-emerald-600' : rate >= 70 ? 'text-amber-600' : 'text-red-600'}`}>
                                {rate}%
                            </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Top endpoints */}
                <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
                  <div className="px-6 py-5 border-b border-slate-100 flex items-center gap-3">
                    <ChartBarIcon className="w-5 h-5 text-slate-400" />
                    <h3 className="text-base font-bold text-slate-900">Top Endpoints</h3>
                  </div>
                  {data.top_endpoints.length === 0 ? (
                    <div className="px-6 py-10 text-center text-slate-400 text-sm font-medium">No endpoint data yet.</div>
                  ) : (
                    <div className="divide-y divide-slate-50">
                      {data.top_endpoints.map((ep, i) => {
                        const maxEp = data.top_endpoints[0]?.count || 1;
                        const epCount = ep.count ?? 0;
                        return (
                          <div key={ep.endpoint} className="flex items-center gap-4 px-6 py-4 hover:bg-slate-50 transition-colors">
                            <span className="text-[11px] font-black text-slate-500 w-4 shrink-0">{i + 1}</span>
                            <div className="flex-1 min-w-0">
                              <p className="font-mono font-bold text-slate-700 text-[13px] truncate">/{ep.endpoint}</p>
                              <div className="flex items-center gap-2 mt-1">
                                <div className="flex-1 h-1 bg-slate-100 rounded-full overflow-hidden">
                                  <div
                                    className="h-full bg-brand-400 rounded-full"
                                    style={{ width: `${Math.round((epCount / maxEp) * 100)}%` }}
                                  />
                                </div>
                                <span className="text-[11px] font-bold text-slate-500 shrink-0">{epCount.toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Zero state tip */}
              {data.total_requests === 0 && (
                <div className="bg-brand-50 border border-brand-100 rounded-2xl p-6 flex items-start gap-4">
                  <CheckCircleIcon className="w-5 h-5 text-brand-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="font-bold text-slate-900 text-sm">No API traffic recorded yet</p>
                    <p className="text-slate-700 text-sm mt-1 leading-relaxed">
                      Create an API key on the <Link to="/dashboard/api-access" className="underline font-bold text-brand-600">API Access</Link> page,
                      then make requests using the <code className="bg-slate-100 px-1 rounded font-mono text-xs text-slate-900">api_token</code> header.
                      All traffic will appear here in real-time.
                    </p>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
